import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Radio, Upload, CalendarPlus, DollarSign, Users, Video, Eye, Heart, TrendingUp, Wallet, ShieldCheck, AlertCircle, BarChart3 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { money } from "../lib/api";
import { toast } from "sonner";

const Stat = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="border border-black/10 bg-white p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-black/50"><Icon className="w-4 h-4" /><span className="text-[11px] font-bold uppercase tracking-wider">{label}</span></div>
    <p className="text-2xl font-black font-display mt-2">{value}</p>
    {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

const FunnelTable = ({ title, rows, testid }) => (
  <div className="mb-4" data-testid={testid}>
    <p className="text-xs font-bold uppercase tracking-wider text-black/50 mb-2">{title}</p>
    {(!rows || rows.length === 0) ? <p className="text-xs text-muted-foreground">No data yet.</p> : (
      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-taven-gray/40 text-black/50"><tr>
            <th className="text-left p-2 font-bold">Title</th><th className="p-2">Views</th><th className="p-2">Clicks</th><th className="p-2">Carts</th><th className="p-2">Sales</th><th className="p-2">Conv</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-black/5"><td className="p-2 font-medium max-w-[160px] truncate">{r.title}</td><td className="p-2 text-center">{r.views}</td><td className="p-2 text-center">{r.click}</td><td className="p-2 text-center">{r.add_to_cart}</td><td className="p-2 text-center font-bold text-taven-orange">{r.confirmed}</td><td className="p-2 text-center">{r.conversion}%</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default function CreatorStudio() {
  const { user, authApi } = useAuth();
  const nav = useNavigate();
  const [host, setHost] = useState(undefined);
  const [earn, setEarn] = useState(null);
  const [videos, setVideos] = useState([]);
  const [subs, setSubs] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [range, setRange] = useState("all");

  useEffect(() => { if (user) authApi.get(`/creator/analytics?range=${range}`).then(({ data }) => setAnalytics(data)).catch(() => {}); }, [user, range]);

  const load = useCallback(async () => {
    if (!user) return;
    try { const { data } = await authApi.get("/live/host/me"); setHost(data.host); } catch { setHost(null); }
    authApi.get("/creator/earnings").then(({ data }) => setEarn(data)).catch(() => {});
    authApi.get("/creator/videos/mine").then(({ data }) => setVideos(data.videos || [])).catch(() => {});
    authApi.get("/creator/subscribers").then(({ data }) => setSubs(data.count || 0)).catch(() => {});
    authApi.get("/live/host/sessions").then(({ data }) => setSessions(data.sessions || [])).catch(() => {});
    authApi.get("/creator/earnings/payouts").then(({ data }) => setPayouts(data.payouts || [])).catch(() => {});
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const goLive = async () => { try { const { data } = await authApi.post("/live/sessions", { title: "TAVEN Live Session" }); nav(`/live/${data.id}`); } catch { toast.error("Could not start."); } };
  const requestPayout = async () => {
    try { const { data } = await authApi.post("/creator/earnings/payout"); toast.success(`Payout requested (${money(data.amount)}) — ${data.status.replace("_", " ")}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not request payout."); }
  };

  if (!user) return <div className="py-24 text-center"><p className="text-sm text-muted-foreground mb-3">Sign in to open Creator Studio.</p><Link to="/login" state={{ from: "/studio" }} className="bg-taven-gold text-black px-5 py-2.5 text-xs font-bold uppercase tracking-wider">Sign in</Link></div>;
  if (host === undefined) return <div className="py-24 text-center text-muted-foreground">Loading Studio…</div>;
  if (!host || host.status !== "approved") return (
    <div className="py-24 text-center max-w-md mx-auto px-4">
      <ShieldCheck className="w-12 h-12 text-taven-gold mx-auto mb-3" />
      <h1 className="text-xl font-black font-display mb-2">Become a TAVEN Creator</h1>
      <p className="text-sm text-muted-foreground mb-4">Apply on the Live page to unlock Creator Studio — go live, post videos, and earn from real sales, gifts and subscriptions.</p>
      <Link to="/live" className="bg-taven-gold text-black px-5 py-2.5 text-xs font-bold uppercase tracking-wider">Go to Live</Link>
    </div>
  );

  const bal = earn?.balances || {};
  const eng = earn?.engagement || {};

  return (
    <div className="mx-auto max-w-6xl px-4 md:px-8 py-8" data-testid="creator-studio">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div><h1 className="text-2xl sm:text-3xl font-black font-display">Creator Studio</h1><p className="text-sm text-muted-foreground">{user.first_name || user.email} · <Link to={`/creator/${encodeURIComponent(user.email)}`} className="text-taven-gold hover:underline">View public profile</Link></p></div>
        <div className="flex flex-wrap gap-2">
          <button onClick={goLive} data-testid="studio-go-live" className="flex items-center gap-1.5 bg-taven-gold text-black px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-black hover:text-white"><Radio className="w-4 h-4" /> Go Live</button>
          <Link to="/videos" data-testid="studio-upload" className="flex items-center gap-1.5 border border-taven-gold text-taven-black px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-taven-gold"><Upload className="w-4 h-4" /> Post Video</Link>
          <Link to="/live" data-testid="studio-schedule" className="flex items-center gap-1.5 border border-black/15 px-4 py-2.5 text-xs font-bold uppercase tracking-wider hover:border-taven-gold"><CalendarPlus className="w-4 h-4" /> Schedule</Link>
        </div>
      </div>

      {/* Earnings */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <Stat icon={Wallet} label="Available" value={money(bal.available || 0)} sub="Ready to request" testid="stat-available" />
        <Stat icon={DollarSign} label="Pending" value={money(bal.pending || 0)} sub="Est. commissions" testid="stat-pending" />
        <Stat icon={TrendingUp} label="Reward Pool (est)" value={money(earn?.estimated_reward_pool_share || 0)} sub="View-share of pool" testid="stat-pool" />
        <Stat icon={DollarSign} label="Paid" value={money(bal.paid || 0)} sub="Settled" testid="stat-paid" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
        <Stat icon={Users} label="Followers" value={eng.followers ?? 0} testid="stat-followers" />
        <Stat icon={Users} label="Subscribers" value={subs} testid="stat-subscribers" />
        <Stat icon={Eye} label="Qualified Views" value={eng.qualified_views ?? 0} testid="stat-views" />
        <Stat icon={Heart} label="Engagement Score" value={eng.engagement_score ?? 0} sub={`${eng.likes||0} likes · ${eng.comments||0} comments`} testid="stat-engagement" />
      </div>

      {/* Payout */}
      <div className="border border-black/10 bg-white p-4 mb-8 flex items-center justify-between flex-wrap gap-3" data-testid="payout-box">
        <div>
          <p className="font-bold text-sm">Request a Payout</p>
          <p className="text-xs text-muted-foreground">Min {money(earn?.config?.min_payout || 0)}. Admin-approved. {earn?.eligibility?.eligible ? "You're eligible." : (earn?.eligibility?.reasons || []).join("; ")}</p>
        </div>
        <button onClick={requestPayout} disabled={!earn?.eligibility?.eligible || (bal.available || 0) < (earn?.config?.min_payout || 0)} data-testid="request-payout-btn" className="bg-taven-orange text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider hover:bg-[#d9561a] disabled:opacity-50 disabled:cursor-not-allowed">Request Payout</button>
      </div>

      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-taven-gray/40 p-3 mb-8"><AlertCircle className="w-4 h-4 text-taven-gold shrink-0 mt-0.5" /><span>{earn?.disclaimer}</span></div>

      {/* Shop the Feed analytics */}
      {analytics && (
        <div className="mb-8" data-testid="creator-analytics">
          <h2 className="text-lg font-black font-display uppercase tracking-wide mb-1 flex items-center gap-2"><BarChart3 className="w-5 h-5 text-taven-gold" /> Shop the Feed</h2>
          <p className="text-xs text-muted-foreground mb-3">See which product videos & lives convert best — post more of what sells.</p>
          <div className="flex gap-1.5 mb-4" data-testid="creator-range-filter">
            {[["7d", "7 Days"], ["30d", "30 Days"], ["all", "All-time"]].map(([k, l]) => (
              <button key={k} onClick={() => setRange(k)} data-testid={`creator-range-${k}`} className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full transition-colors ${range === k ? "bg-taven-black text-white" : "bg-taven-gray/50 text-black/60 hover:text-black"}`}>{l}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
            {[["Views", analytics.totals.views], ["Clicks", analytics.totals.click], ["Carts", analytics.totals.add_to_cart], ["Checkouts", analytics.totals.checkout], ["Sales", analytics.totals.confirmed], ["Conv %", `${analytics.totals.conversion}%`], ["Pending", money(analytics.totals.pending)]].map(([l, v]) => (
              <div key={l} className="border border-black/10 bg-white p-2.5 text-center"><p className="text-[10px] font-bold uppercase tracking-wider text-black/40">{l}</p><p className="text-lg font-black font-display">{v}</p></div>
            ))}
          </div>
          <FunnelTable title="Top Product Videos" rows={analytics.top_videos} testid="top-videos" />
          <FunnelTable title="Top Live Sessions" rows={analytics.top_lives} testid="top-lives" />
        </div>
      )}

      {/* Videos */}
      <h2 className="text-lg font-black font-display uppercase tracking-wide mb-3 flex items-center gap-2"><Video className="w-5 h-5 text-taven-gold" /> My Videos ({videos.length})</h2>
      {videos.length === 0 ? <p className="text-sm text-muted-foreground mb-8">No videos yet. <Link to="/videos" className="text-taven-gold">Post your first video</Link>.</p> : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="my-videos">
          {videos.map((v) => (
            <div key={v.id} className="border border-black/10 bg-white">
              <div className="aspect-[9/16] bg-taven-black relative">{v.thumbnail && <img src={v.thumbnail} alt="" className="w-full h-full object-cover opacity-80" />}</div>
              <div className="p-2"><p className="text-xs font-bold line-clamp-1">{v.caption || "Untitled"}</p><p className="text-[11px] text-muted-foreground mt-0.5">{v.views||0} views · {v.likes||0} likes · {v.comments||0} comments</p></div>
            </div>
          ))}
        </div>
      )}

      {/* Payout history */}
      {payouts.length > 0 && (
        <>
          <h2 className="text-lg font-black font-display uppercase tracking-wide mb-3">Payout History</h2>
          <div className="border border-black/10 bg-white divide-y divide-black/5" data-testid="payout-history">
            {payouts.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-4 py-3">
                <span className="text-sm font-bold">{money(p.amount)}</span>
                <span className={`text-[10px] font-bold uppercase px-2 py-1 ${p.status === "paid" ? "bg-green-100 text-green-700" : p.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{p.status.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
